import React, { Fragment, ReactElement, useEffect, useState } from 'react';
import API from '@sesame/web-api';

import { ILoadState, IRack, IArenaPart, IArenaRevision, IComponent, INodeListDisplaySummary } from 'src/types';
import { Spinner } from 'src/components/Spinner';
import Alert, { AlertTypes } from 'src/components/AlertDisplay';

import { AddRack } from './MaterialComponents/AddRack';
import { AddLooseNode } from './MaterialComponents/AddLooseNode';
import { EditLooseNodes } from './MaterialComponents/EditLooseNodes'
import { RemoveRack } from './MaterialComponents/RemoveRack';
import { EditRack } from './MaterialComponents/EditRack';
import { DuplicateRack } from './MaterialComponents/DuplicateRack';
import { useAuth0 } from '@auth0/auth0-react';

interface ISalesorderMaterialProps {
  salesorderId: number;
}


type nodeMap = Map<string, INodeListDisplaySummary>

export const SalesorderMaterial = ({ salesorderId }: ISalesorderMaterialProps) => {

  const  {isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [salesOrderRackList, setSalesOrderRackList] = useState<IRack[]>([]);
  const [displayState, setDisplayState] = useState<ILoadState>(ILoadState.NEW);
  const [looseNodeDisplayState, setLooseNodeDisplayState] = useState<ILoadState>(ILoadState.NEW);
  const [looseNodeRackId, setLooseNodeRackId] = useState(0);
  const [looseComponents, setLooseComponents] = useState<nodeMap>(new Map());

  const getSalesorderRacks = () => {
    getAccessTokenSilently().then(accessToken => {
    new Promise((resolve, reject) => {
      resolve(API.get(accessToken, `/api/v1/salesorder/${salesorderId}/rack`));
    })
      .then((resp) => {
        const r = resp as any;
        const racks = r.data as IRack[];
        setSalesOrderRackList(racks);
        setDisplayState(ILoadState.READY);
        console.log('looseNodeRack: sorack length: ' + racks.length);
        const looseNodeRack = racks.find(r => r.bomPartNumber == 'LooseNodes');
        if (looseNodeRack) {
          setLooseNodeRackId(looseNodeRack.rackId);
        }
        return looseNodeRack && API.get(accessToken, `api/v1/rack/${looseNodeRack.rackId}/components`);
      })
      .then(resp => {
        if (resp && resp.data) {
          const components = resp.data as IComponent[];
          const cmap = new Map();
          for (const c of components.filter(c => !c.parentId)) {
            const entry: INodeListDisplaySummary = cmap.get(nodeKey(c)) || { name: '', serialized: [], unserialized: [] };
            entry.name = c.name;
            if (c.serialNumber) {
              entry.serialized.push(c);
            } else {
              entry.unserialized.push(c);
            }
            cmap.set(nodeKey(c), entry);
          }
          setLooseComponents(cmap);
          setLooseNodeDisplayState(ILoadState.READY);
        }
      })
      .catch((err) => setDisplayState(ILoadState.ERROR));
  })
  }
  const onEditSave = async () => {
    getSalesorderRacks();
  }

  const deleteRack = async (rackId: number) => {
    const accessToken = await getAccessTokenSilently();
    API.delete(accessToken, `/api/v1/salesorder/${salesorderId}/rack/${rackId}`, {})
      .then((resp) => {
        Alert(AlertTypes.SUCCESS, "Rack Deleted");
        getSalesorderRacks();
      })
      .catch((err) => Alert(AlertTypes.ERROR, err.message));
  }

  useEffect(() => {
    getSalesorderRacks();
  }, [getAccessTokenSilently]);

  if (displayState == ILoadState.NEW || displayState == ILoadState.LOADING) {
    return <Spinner />
  }
  if (displayState == ILoadState.ERROR) {
    return <div>Error loading rack information</div>
  }

  if (isLoading) {
    return <Spinner />
  }
  //  <AddRack salesorderId={salesorderId} />
  return <><h2 className="uk-card-title">Material
  <span className="uk-align-right"><AddRack salesorderId={salesorderId} onSave={onEditSave} /></span>
  </h2>

    <div>
      <table className="uk-table uk-table-small">
        <tbody>
          {salesOrderRackList.filter(r => r.rackId != looseNodeRackId).map(r => <RackDisplay key={r.rackId} rack={r}
            onDelete={(rackId) => deleteRack(rackId)}
            onEditSave={() => onEditSave()} />
          )}
        </tbody>
      </table>
    </div >
    <span className="uk-align-right">
      <AddLooseNode
        salesorderId={salesorderId}
        rackId={looseNodeRackId}
        onSave={onEditSave} />
    </span>
    <div>
      <table className="uk-table uk-table-small">
        <tbody>
          {[...looseComponents.keys()].sort().map((c) => <NodeDisplay
            key={c}
            partNumber={c}
            nodes={looseComponents.get(c)!}
            onEditSave={() => onEditSave()} />
          )}
        </tbody>
      </table>
    </div>
  </>
}


interface IRackDisplayProps {
  rack: IRack;
  onDelete: (rackId: number) => Promise<void>;
  onEditSave: () => Promise<void>;
}

const RackDisplay = ({ rack, onDelete, onEditSave }: IRackDisplayProps) => {
  //  const rackCounts = ({ entered, count }: { entered: number, count: number }) => {
  const rackCounts = () => {
    if (rack.componentCount && +rack.componentCount > 0) {
      return <>{rack.componentsEntered} / {rack.componentCount}</>
    } else {
      return <span className="uk-text-muted">pending</span>
    }
  }

  return <tr>
    <td><a href={`/rack?rack=${rack.rackId}`}>{rack.identifier}</a></td>
    <td className="uk-text-nowrap">{rack.bomPartNumber} : {rack.revision}</td>
    <td><span className="uk-marign-left uk-margin-right">{rackCounts()}</span></td>
    <td> <span className="uk-text-muted">{rack.description}</span> </td>
    <td className="materialActionBar"><RemoveRack
      rackId={rack.rackId}
      rackIdentifier={rack.identifier}
      onDelete={() => onDelete(rack.rackId)}
    />
      <EditRack
        rack={rack}
        onEditSave={() => onEditSave()}
      />
      <DuplicateRack
        rack={rack}
        onEditSave={() => onEditSave()}
      />
      <a href={`/rackElevation?rack=${rack.rackId}`} data-uk-icon="server-rack" />
      <a href={`/rackSerials?rack=${rack.rackId}`} data-uk-icon="scan-serial" /></td>
  </tr >
}


interface INodeDisplayProps {
  partNumber: string;
  nodes: INodeListDisplaySummary;
  onEditSave: () => Promise<void>;
}

const NodeDisplay = ({ partNumber, nodes, onEditSave }: INodeDisplayProps) => {

  const total = nodes.serialized.length + nodes.unserialized.length
  const sampleNode = nodes.serialized.length ? nodes.serialized[0] : nodes.unserialized[0];

  return <tr>
    <td className="uk-table-shrink uk-text-nowrap">{sampleNode.partNumber} : {sampleNode.revision}</td>
    <td className="uk-table-shrink uk-text-nowrap">
      {nodes.serialized.length} / {total}
    </td>
    <td className="uk-table-expand uk-text-truncate">{sampleNode.name}</td>
    <td className="materialActionBar"><EditLooseNodes
      partNumber={partNumber}
      nodes={nodes}
      onEditSave={() => onEditSave()}
    />
      <a href={`/ScanNodes?rack=${sampleNode.rackId}`} data-uk-icon="scan-serial" />
    </td>
  </tr >
}


const nodeKey = (node: IComponent): string => {
  return node.partNumber + ':' + node.revision;
}
